import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { FieldGroup, PillButton, Row, Select, Stack, Text, Title, TextInput } from 'library/ui';
import { client, consoleLogInDev, isValidZipCode, isValidFormInputString } from 'shared';
import States from 'constants/States';

import { useUserContext } from 'UserContext';
import useContact from 'parent-portal/profile/BusinessProfile/hooks/useContact';

import ProgramTypes from './ProgramTypes';
import { getProgramTypes, IProgramType } from 'api/ProgramTypeApi';
import LocationTypes from 'parent-portal/BusinessLocation/LocationTypes';

const stateOptions = Object.values(States).map((name) => ({ key: name, value: name }));

const DEFAULT_NEW_CONTACT = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    contactPreference: 'EMAIL',
};

type LocationDetailKey = 'street' | 'city' | 'state' | 'zip' | 'name' | 'programId';

interface LocationDetails {
    [key: string]: { value: string; error: string | undefined; displayHelper: boolean };
}

const DEFAULT_LOCATION: LocationDetails = {
    street: { value: '', error: undefined, displayHelper: false },
    city: { value: '', error: undefined, displayHelper: false },
    state: { value: '', error: undefined, displayHelper: false },
    zip: { value: '', error: undefined, displayHelper: false },
    name: { value: '', error: undefined, displayHelper: false },
    programId: { value: '', error: undefined, displayHelper: false },
};

export default function AddBusinessLocationForm({
    cancel,
    complete,
    optionalContact,
}: {
    cancel: () => void;
    complete?: () => void;
    optionalContact?: boolean;
}) {
    const { refreshAlreadyLoggedInUser } = useUserContext();

    const { isNewContactValid, populateCurrentContactInfo } = useContact(DEFAULT_NEW_CONTACT, optionalContact);

    const [locationDetails, setLocationDetails] = useState<LocationDetails>(DEFAULT_LOCATION);
    const [programTypeOptions, setProgramTypeOptions] = useState<IProgramType[]>([]);
    const [programTypes, setProgramTypes] = useState<string[]>([]);
    const [locationTypes, setLocationTypes] = useState<number[]>([]);
    const [errorMessage, setErrorMessage] = useState('');

    const programIdsRequiredStates = ['ohio', 'illinois'];

    useEffect(() => {
        if (!locationDetails.state.value) {
            if (programTypes.length) {
                setProgramTypes([]);
            }
        } else {
            getProgramTypes(locationDetails.state.value)
                .then((response) => {
                    setProgramTypeOptions(response);
                    if (!response.length) {
                        setProgramTypes([]);
                    }
                })
                .catch(consoleLogInDev);
        }
    }, [locationDetails.state.value]);

    function checkIsError(key: LocationDetailKey, value: string) {
        if (key === 'name') {
            return !isValidFormInputString(value) ? 'Please enter a valid name.' : undefined;
        } else if (['street', 'city'].includes(key)) {
            return !isValidFormInputString(value) ? 'Please enter a valid street or city.' : undefined;
        } else if (key === 'state') {
            return !isValidFormInputString(value) ? 'Please enter a valid state.' : undefined;
        } else if (key === 'zip') {
            return !isValidZipCode(value) ? 'Please enter a valid zip code.' : undefined;
        } else if (key === 'programId') {
            return programIdsRequiredStates.includes(locationDetails.state.value?.toLowerCase() ?? '')
                ? !(value.length > 5 && value.length < 21)
                    ? 'Please enter a valid program ID.'
                    : undefined
                : undefined;
        }

        return undefined;
    }

    function setValue(key: LocationDetailKey, value: string) {
        const additionalChecks =
            key === 'state' && programIdsRequiredStates.includes(value?.toLowerCase() ?? '')
                ? {
                      programId: {
                          value: locationDetails.programId.value,
                          error: checkIsError('programId', value),
                          displayHelper: false,
                      },
                  }
                : {
                      programId: {
                          value: locationDetails.programId.value,
                          error: checkIsError('programId', value),
                          displayHelper: false,
                      },
                  };

        setLocationDetails({
            ...locationDetails,
            ...additionalChecks,
            ...{ [key]: { value, error: checkIsError(key, value), displayHelper: false } },
        });
    }

    function setDisplay(key: LocationDetailKey) {
        const error = checkIsError(key, locationDetails[`${key}`].value);
        setLocationDetails({
            ...locationDetails,
            ...{ [key]: { ...locationDetails[`${key}`], error, displayHelper: !!error } },
        });
    }

    function checkBeforeSubmit() {
        const newLocationDetails = { ...locationDetails };
        Object.keys(locationDetails).forEach((key: string) => {
            const error = checkIsError(key as LocationDetailKey, locationDetails[`${key as LocationDetailKey}`].value);
            newLocationDetails[`${key as LocationDetailKey}`] = {
                ...newLocationDetails[`${key as LocationDetailKey}`],
                error,
                displayHelper: !!error,
            };
        });
        setLocationDetails(newLocationDetails);

        return Object.values(newLocationDetails).every((x) => !x.error) && isNewContactValid();
    }

    function onAddAddress() {
        if (!checkBeforeSubmit()) return;

        client('api/business-location/', {
            body: {
                street: locationDetails.street.value,
                city: locationDetails.city.value,
                state: locationDetails.state.value,
                zip_code: locationDetails.zip.value,
                location_name: locationDetails.name.value,
                program_id: locationDetails.programId.value,
                program_types: programTypeOptions.filter((x) => programTypes.includes(x.name)).map((x) => x.id),
                location_types: locationTypes,
            },
        })
            .then((res) => {
                if (res.status === 500) {
                    setErrorMessage(res.message);
                } else {
                    refreshAlreadyLoggedInUser(onCancel);
                }
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    }

    function onCancel() {
        complete?.();
        setLocationDetails(DEFAULT_LOCATION);
        populateCurrentContactInfo(DEFAULT_NEW_CONTACT);
        cancel();
    }

    function onProgramTypesChanges(option: string) {
        if (programTypes.includes(option)) {
            setProgramTypes(programTypes.filter((x) => x !== option));
        } else {
            setProgramTypes([...programTypes, option]);
        }
    }

    function getProgramTypesSection() {
        if (!programTypeOptions.length) {
            return;
        }

        return (
            <Stack gap="sm">
                <Text>Do you currently participate in any of the following state programs?</Text>
                <ProgramTypes
                    options={programTypeOptions.map((x) => x.name)}
                    programTypes={programTypes}
                    onProgramTypesChanged={(x) => onProgramTypesChanges(x)}
                />
            </Stack>
        );
    }

    const firstFields: LocationDetailKey[] = ['name', 'street', 'city'];
    const secondFields: LocationDetailKey[] = ['zip', 'programId'];
    const validLocation = Object.values(locationDetails).every((x) => !x.error) && isNewContactValid();

    return (
        <Container>
            <Stack gap="md">
                <Title size="h1">Add a Center Location</Title>
                {firstFields.map((field: LocationDetailKey) => (
                    <FieldGroup key={field} label={`${field === 'name' ? 'Location Name' : `${field.capitalize()} *`}`}>
                        <TextInput
                            error={locationDetails[`${field}`].error}
                            onChange={(value: string) => setValue(field, value)}
                            onBlur={() => setDisplay(field)}
                            value={locationDetails[`${field}`].value}
                        />
                    </FieldGroup>
                ))}
                <FieldGroup label="State" required>
                    <Select
                        value={locationDetails.state.value || ''}
                        onChange={(v) => setValue('state', v ?? '')}
                        options={stateOptions}
                        placeholder="Select a state"
                        fullWidth
                    />
                </FieldGroup>
                {secondFields.map((field: LocationDetailKey) => (
                    <FieldGroup key={field} label={field === 'zip' ? 'Zip *' : 'Program ID **'}>
                        <TextInput
                            error={locationDetails[`${field}`].error}
                            onChange={(value: string) => setValue(field, value)}
                            onBlur={() => setDisplay(field)}
                            value={locationDetails[`${field}`].value}
                        />
                    </FieldGroup>
                ))}
                {getProgramTypesSection()}
                <LocationTypes selectedTypes={locationTypes} setSelectedTypes={setLocationTypes} />
                <Text size="sm" tone="secondary">
                    * Required
                </Text>
                <Text size="sm" tone="secondary">
                    ** Required in the state of Ohio and Illinois
                </Text>
                <Row justify="end" gap="sm">
                    <PillButton variant="secondary" onClick={onCancel}>
                        Cancel
                    </PillButton>
                    <PillButton disabled={!validLocation} onClick={onAddAddress}>
                        Add
                    </PillButton>
                </Row>
                {errorMessage && <Text tone="error">{errorMessage}</Text>}
            </Stack>
        </Container>
    );
}

const Container = styled('div')({
    padding: 30,
});
